import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Bell, Moon, Sun, Wallet, LogOut, User, ShoppingBag, Heart, Receipt, LayoutDashboard, Shield, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, naira } from "@/lib/api";

export function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("gm_theme") !== "light");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("gm_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export function SearchBar({ big = false, testid = "nav-search-input", initial = "" }) {
  const [q, setQ] = useState(initial);
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); nav(`/explore?q=${encodeURIComponent(q.trim())}`); };
  return (
    <form onSubmit={submit} className="relative w-full">
      <Search size={big ? 20 : 16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input data-testid={testid} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search flyers, banners, logos, invitations..."
        className={`w-full rounded-full border border-border bg-card pl-11 pr-4 outline-none transition-colors focus:border-primary ${big ? "h-14 text-base shadow-2xl shadow-primary/10 pr-28" : "h-10 text-sm"}`} />
      {big && <Button data-testid="hero-search-btn" type="submit" className="absolute right-1.5 top-1.5 h-11 rounded-full px-6">Search</Button>}
    </form>
  );
}

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  return (
    <Link to="/notifications" data-testid="nav-notifications-btn" className="relative grid h-9 w-9 place-items-center rounded-full hover:bg-secondary">
      <Bell size={18} />
      {data.unread > 0 && <span data-testid="notif-unread-count" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{data.unread}</span>}
    </Link>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [bal, setBal] = useState(null);
  useEffect(() => { if (user.is_seller) api.get("/seller/wallet").then((r) => setBal(r.data.wallet.available_balance)).catch(() => {}); }, [user]);
  const go = (p) => () => nav(p);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-border py-1 pl-1 pr-3 hover:border-primary">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-primary text-xs font-bold text-white">{user.name[0]?.toUpperCase()}</span>
          {bal != null ? <span className="hidden font-mono-ui text-xs sm:inline" data-testid="nav-wallet-balance">{naira(bal)}</span>
            : <span className="hidden max-w-[90px] truncate text-xs sm:inline">{user.name.split(" ")[0]}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel><p className="truncate">{user.name}</p><p className="truncate text-xs font-normal text-muted-foreground">{user.email}</p></DropdownMenuLabel>
        <DropdownMenuSeparator />
        {user.is_seller && <DropdownMenuItem data-testid="menu-seller-dashboard" onClick={go("/seller")}><LayoutDashboard size={15} className="mr-2" />Seller dashboard</DropdownMenuItem>}
        {user.is_seller && <DropdownMenuItem data-testid="menu-wallet" onClick={go("/seller/wallet")}><Wallet size={15} className="mr-2" />Wallet</DropdownMenuItem>}
        {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={go("/admin")}><Shield size={15} className="mr-2" />Admin dashboard</DropdownMenuItem>}
        <DropdownMenuItem data-testid="menu-purchases" onClick={go("/purchases")}><ShoppingBag size={15} className="mr-2" />My purchases</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-favorites" onClick={go("/favorites")}><Heart size={15} className="mr-2" />My favorites</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-orders" onClick={go("/orders")}><Receipt size={15} className="mr-2" />Order history</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-profile" onClick={go("/profile")}><User size={15} className="mr-2" />Profile</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut size={15} className="mr-2" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Navbar() {
  const { user } = useAuth();
  const [dark, setDark] = useTheme();
  const link = ({ isActive }) => `text-sm font-medium transition-colors hover:text-foreground ${isActive ? "text-foreground" : "text-muted-foreground"}`;
  return (
    <header className="glass sticky top-0 z-40 border-b">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-2 px-3 sm:gap-4 sm:px-6">
        <Logo />
        <nav className="ml-6 hidden items-center gap-6 lg:flex">
          <NavLink to="/explore" className={link} data-testid="nav-explore-link">Explore</NavLink>
          <NavLink to="/explore?sort=popular" className={link} data-testid="nav-popular-link">Popular</NavLink>
          <NavLink to="/seller" className={link} data-testid="nav-sell-link">Sell</NavLink>
        </nav>
        <div className="mx-auto hidden w-full max-w-md md:block"><SearchBar /></div>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <Link to="/explore" className="grid h-9 w-9 place-items-center rounded-full hover:bg-secondary md:hidden" data-testid="nav-mobile-search"><Search size={18} /></Link>
          <button data-testid="theme-toggle" onClick={() => setDark(!dark)} className={`${user ? "" : "hidden sm:grid"} grid h-9 w-9 place-items-center rounded-full hover:bg-secondary`}>
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {user && <NotificationBell />}
          {user ? (
            <>
              <Button asChild size="sm" className="hidden rounded-full lg:inline-flex" data-testid="nav-become-seller-btn">
                <Link to={user.is_seller ? "/seller/upload" : "/seller"}><Plus size={15} className="mr-1" />{user.is_seller ? "Sell a Template" : "Become a Seller"}</Link>
              </Button>
              <UserMenu />
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="rounded-full px-2 sm:px-3" data-testid="nav-login-btn"><Link to="/login">Log in</Link></Button>
              <Button asChild size="sm" className="rounded-full" data-testid="nav-signup-btn"><Link to="/register">Sign up</Link></Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
